import { HttpContextToken } from '@angular/common/http';
import { InjectionToken } from '@angular/core';

export type AppConfig = {
  project: {
    name:         string;
    officialName: string;
    city:         string;
    description:  string;
    address:      string;
    support:      string;
    phone:        string[];
    website:      string;
    logo:         string;
    banner:       string;
    favicon:      string;
  };
  smartDOM: {
    name:      string;
    logoBlack: string;
    logoWhite: string;
  };
  api: {
    baseUrl:            string;
    maestroCalleUrl:    string;
    claveUnicaEndpoint: string;
    appUrl:             string;
  };
  client: {
    name:               string;
    fileUploadProvider: 'dropbox' | 'azure';
    upload: {
      maxFileSize:       number;
      maxTotalSize:      number;
      allowedExtensions: string[];
    };
  };
  banderas: {
    claveUnica:           boolean;
    expedientesCompletos: boolean;
  };
  theme?: {
    primary:             string;
    primaryHover:        string;
    primaryDisabledBg:   string;
    primaryDisabledText: string;
    accent:              string;
    accentHover:         string;
    surface:             string;
    surfaceMuted:        string;
    borderMuted:         string;
    textMain:            string;
    textSecondary:       string;
    primePrimary:        string;
    primePrimarySoft:    string;
  };
};

export const APP_CONFIG            = new InjectionToken<AppConfig>('APP_CONFIG');
export const API_BASE_URL          = new InjectionToken<string>('API_BASE_URL');
export const API_BASE_MAESTRO_CALLE = new InjectionToken<string>('API_BASE_MAESTRO_CALLE');
export const ADD_AUTH              = new HttpContextToken<boolean>(() => false);

/**
 * Resuelve una ruta de branding (project.logo / project.banner) contra api.baseUrl.
 * - Si ya es una URL absoluta (http/https, ej. un cliente que aloja su logo en un
 *   CDN aparte), se deja tal cual.
 * - Si es una ruta relativa (ej. "branding/logo.png", servida como estático por el
 *   backend de esta municipalidad — ver smartdom-backend/public/branding), se arma
 *   la URL final combinándola con api.baseUrl. Así la URL del logo sigue automáticamente
 *   al ambiente/cliente sin quedar hardcodeada por separado en /public/frontend-config.
 */
export function resolveBrandingUrl(pathOrUrl: string | undefined, baseUrl: string | undefined): string {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

  const base = String(baseUrl || '').replace(/\/+$/, '');
  const path = pathOrUrl.replace(/^\/+/, '');
  return base ? `${base}/${path}` : path;
}

/** "#3758F9" -> "55 88 249" (formato que espera rgb(var(--x) / alpha) en tailwind.config.js). */
function hexToRgbChannels(hex: string): string | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)} ${parseInt(g, 16)} ${parseInt(b, 16)}`;
}

/** Setea la paleta de marca como CSS custom properties, leídas por tailwind.config.js y styles.scss. */
export function applyTheme(theme?: AppConfig['theme']): void {
  if (!theme) return;
  const root = document.documentElement.style;

  const rgb = (cssVar: string, hex: string) => {
    const channels = hexToRgbChannels(hex);
    if (channels) root.setProperty(cssVar, channels);
  };

  // Colores de Tailwind (soportan modificador de opacidad, ej. bg-brand-primary/20):
  // se setean como canales RGB, no como hex — ver brandColor() en tailwind.config.js.
  rgb('--brand-primary-rgb', theme.primary);
  rgb('--brand-primary-hover-rgb', theme.primaryHover);
  rgb('--brand-primary-disabled-bg-rgb', theme.primaryDisabledBg);
  rgb('--brand-primary-disabled-text-rgb', theme.primaryDisabledText);
  rgb('--brand-accent-rgb', theme.accent);
  rgb('--brand-accent-hover-rgb', theme.accentHover);
  rgb('--brand-surface-rgb', theme.surface);
  rgb('--brand-surface-muted-rgb', theme.surfaceMuted);
  rgb('--brand-border-muted-rgb', theme.borderMuted);
  rgb('--brand-text-main-rgb', theme.textMain);
  rgb('--brand-text-secondary-rgb', theme.textSecondary);

  // Variables de PrimeNG: se consumen como hex directo (color-mix()/uso plano), no vía Tailwind.
  root.setProperty('--primary-color', theme.primePrimary);
  root.setProperty('--p-checkbox-highlight-background', theme.primePrimarySoft);
}

/**
 * Reemplaza el favicon en runtime por el de esta municipalidad (project.favicon,
 * ya resuelto por resolveBrandingUrl). index.html trae uno genérico como fallback
 * para el instante antes de que /public/frontend-config responda; esta función lo
 * pisa apenas hay uno específico del cliente.
 */
export function applyFavicon(faviconUrl?: string): void {
  if (!faviconUrl) return;

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}
